import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyEmail } from '../services/authService';
import styles from '../styles/SignIn.module.css';
import headerStyles from '../styles/modules/Header.module.css';

type VerifyEmailResult = {
    ok: boolean;
};

const verifyEmailRequests = new Map<string, Promise<VerifyEmailResult>>();

const verifyEmailOnce = (token: string): Promise<VerifyEmailResult> => {
    const existingRequest = verifyEmailRequests.get(token);
    if (existingRequest) {
        return existingRequest;
    }

    const request = verifyEmail(token)
        .then(() => ({
            ok: true
        }))
        .catch(() => ({
            ok: false
        }));

    verifyEmailRequests.set(token, request);
    return request;
};

const VerifyEmail: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!successMessage) {
            return undefined;
        }

        const redirectTimer = window.setTimeout(() => {
            navigate('/signin');
        }, 2000);

        return () => {
            window.clearTimeout(redirectTimer);
        };
    }, [navigate, successMessage]);

    useEffect(() => {
        let active = true;

        const runVerification = async () => {
            if (!token) {
                if (active) {
                    setError(t('auth.invalidVerificationToken'));
                    setLoading(false);
                }
                return;
            }

            setLoading(true);
            setError('');

            try {
                const result = await verifyEmailOnce(token);
                if (active) {
                    if (result.ok) {
                        setSuccessMessage(t('auth.verifyEmailSuccess'));
                    } else {
                        setError(t('auth.verifyEmailFailed'));
                    }
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        runVerification();

        return () => {
            active = false;
        };
    }, [token, t]);

    return (
        <div className={styles.signinPage}>
            <header className={styles.header}>
                <button
                    className={`${headerStyles.backButton} ${headerStyles.backButtonAbsolute}`}
                    onClick={() => navigate('/signin')}
                    title={t('auth.backToSignIn')}
                    aria-label={t('auth.backToSignIn')}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1 className={styles.headerTitle}>{t('auth.verifyEmailTitle')}</h1>
            </header>

            <div className={styles.signinContainer}>
                <div className={styles.signinContent}>
                    <div className={styles.signinCard}>
                        <h3 className={styles.cardTitle}>{t('auth.verifyEmailTitle')}</h3>

                        {loading && <div className={styles.signupSection}>{t('auth.verifyingEmail')}</div>}
                        {!loading && error && <div className={styles.errorMessage}>{error}</div>}
                        {!loading && !error && successMessage && (
                            <div className={styles.signupSection}>
                                <div>{successMessage}</div>
                                <div>{t('auth.redirectingToSignIn')}</div>
                            </div>
                        )}

                        <div className={styles.toggleMode}>
                            <p>
                                <button onClick={() => navigate('/signin')} className={styles.toggleLink}>
                                    {t('auth.backToSignIn')}
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;
